import { useCallback, useRef } from "react";
import { useAppDispatch } from "../app/hooks";
import {
  fetchConditionsAndSlotsAsync,
  setActivePage,
  setIFrameLink,
  setShowFlowBuilder,
  updateBreadcrumbValue,
} from "../app/slice/appSlice";
import {
  addVariant,
  addCondition,
  removeCondition,
  updateCondition,
  updateConditionsBasedOnPriority,
  selectCondition,
  updateVariantStatus,
  selectConditionAsync,
  setSelectedVariantId,
  setSelectedSlot,
} from "../app/slice/conditionSlice";
import { addSlot, removeSlot, updateSlot } from "../app/slice/slotSlice";
import { openMissingEntitiesModal } from "../app/slice/missingEntitiesSlice";
import { LIST_PICKER_ENTITIES } from "../components/MissingEntitiesModal/MissingEntitiesModal";
import { WIDGET_EDITOR_URL } from "../config";
import { openCrispChat, sendSupportMessage } from "../utils/crisp";
import { Condition, Slot } from "../models";
import {
  generateBreadcrumbFormat,
  IFramePostMessageType,
  sendPostMessage,
} from "../utils/helpers";
import { PageType } from "@reconvert/reconvert-utils";
import { RootState, store } from "../app/store";
import { FunctionRegistry } from "../utils/functionRegistry";
import { getToken } from "../services/auth.service";
import { getVariantById } from "../services/variants.service";
import i18n from "../utils/i18n";
import useUnsavedBar from "./useUnsavedBar";
import type {
  ToolOutputsMessageEvent,
  UseAIChatContextReturn,
} from "../types/aiChatBot";
import { useFlowBuilderSession } from "@reconvert/react-ui-component";
import { useVariant } from "@unleash/proxy-client-react";
import { UNLEASH_SUPPORT_CHAT_PROVIDER } from "../utils/constant";

/** Sends ACTION_ACK to the chatbot and clears the sessionStorage flag for the given action type. */
const sendAckIfNeeded = (actionType: string) => {
  if (sessionStorage.getItem(actionType)) {
    sendPostMessage(IFramePostMessageType.ACTION_ACK);
    sessionStorage.removeItem(actionType);
  }
};

/**
 * Centralized all AI Chatbot ↔ Flow Builder communication.
 *
 * Responsibilities:
 * 1. Sends context updates (current view, selected page) to the AI Chatbot via postMessage
 *    so it always knows the user's current position in the Flow Builder.
 * 2. Handles tool-output actions received from the AI Chatbot (via the parent iframe)
 *    such as navigating to a page/condition/variant, opening the widget editor,
 *    and mutating slots. All actions are gated by the unsaved-changes modal — if the
 *    user has pending edits, they must save or discard before the action proceeds.
 *
 * The hook exposes its handler via a ref (processToolOutputsActionsRef) so the
 * existing message listener in useAppInit can call it without adding a function
 * to the useEffect dependency array.
 */
const useAIChatContext = (): UseAIChatContextReturn => {
  const dispatch = useAppDispatch();
  const { preventAction } = useUnsavedBar();
  const { setPageInSession, setSelectedConditionInSession, setSelectedVariantInSession } = useFlowBuilderSession();
  const supportChatProvider = useVariant(UNLEASH_SUPPORT_CHAT_PROVIDER);
  const isCrisp = supportChatProvider.feature_enabled && supportChatProvider.name === "crisp";
  const skipLoadConditionsRef = useRef(false);
  const preventActionRef = useRef(preventAction);

  preventActionRef.current = preventAction;

  // --- Tool-output action handlers (invoked by the AI Chatbot via postMessage) ---

  const executeToolOutputsActions = useCallback(async (event: ToolOutputsMessageEvent) => {
    const { data: eventData } = event;

    if ('actions' in eventData) {
      for (const action of eventData.actions) {
        await executeToolOutputsActions({ data: action });
      }

      return;
    }

    if (eventData.action === 'SELECT_PAGE') {
      const { page } = eventData.data;

      skipLoadConditionsRef.current = true;
      try {
        dispatch(setShowFlowBuilder(true));
        FunctionRegistry.removeQueryParamsFromUrl?.();

        const currentState = store.getState() as RootState;

        if (page && page !== currentState.app.activePage) {
          dispatch(setActivePage(page as PageType));
          await dispatch(fetchConditionsAndSlotsAsync()).unwrap().then(async (res: any) => {
            const { conditions } = res;
            const defaultConditionId = conditions?.data?.find((condition: any) => condition.isDefault === true)?.id;

            if (defaultConditionId) {
              dispatch(selectCondition(defaultConditionId));
              const res = await dispatch(selectConditionAsync(defaultConditionId)).unwrap();

              if (res?.data) {
                dispatch(
                  updateBreadcrumbValue({
                    breadCrumb: generateBreadcrumbFormat({ page: res?.data?.location, condition: res?.data?.name }),
                  }),
                );
              }
            }
          });
        }
      } finally {
        skipLoadConditionsRef.current = false;
        sendAckIfNeeded('SELECT_PAGE');
      }

      return;
    }

    if (eventData.action === 'SELECT_CONDITION') {
      const { conditionId } = eventData.data;

      try {
        const currentState = store.getState() as RootState;

        if (conditionId && currentState.conditions.selectedConditionId !== conditionId) {
          dispatch(selectCondition(conditionId));
          const res = await dispatch(selectConditionAsync(conditionId)).unwrap();

          if (res?.data) {
            dispatch(
              updateBreadcrumbValue({
                breadCrumb: generateBreadcrumbFormat({ page: res?.data?.location, condition: res?.data?.name }),
              }),
            );
          }
        }
      } finally {
        sendAckIfNeeded('SELECT_CONDITION');
      }

      return;
    }

    if (eventData.action === 'SELECT_SLOT') {
      const { slotId } = eventData.data;

      try {
        const currentState = store.getState() as RootState;
        const currentBreadcrumb = currentState.app.breadcrumbValue;
        const updatedSlot = currentState.slots.slots.find((slot) => slot.id === slotId);
        const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
        const page = currentState.app.activePage;
        const condition = splitBreadcrumb[1] ?? '';

        const variant = currentState.conditions.conditions.find((condition) => condition.id === currentState.conditions.selectedConditionId)?.variants?.find((variant: any) => variant.id === (updatedSlot as any)?.variantId);

        if ((updatedSlot as any)?.variantId) {
          dispatch(setSelectedVariantId((updatedSlot as any)?.variantId));
          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({ page, condition, slot: updatedSlot?.name, variant: variant?.name }),
            }),
          );
        }
      } finally {
        sendAckIfNeeded('SELECT_SLOT');
      }

      return;
    }

    if (eventData.action === 'SELECT_VARIANT') {
      const currentState = store.getState() as RootState;
      const currentBreadcrumb = currentState.app.breadcrumbValue;
      const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
      const page = splitBreadcrumb[0] ?? '';
      const condition = splitBreadcrumb[1] ?? '';
      const slot = splitBreadcrumb[2] ?? '';
      const { variantId } = eventData.data;

      try {
        if (variantId) {
          const selectedCondition = currentState.conditions.conditions.find((condition) => condition.id === currentState.conditions.selectedConditionId);
          const updatedVariant = selectedCondition?.variants?.find((variant: any) => variant.id === variantId);

          dispatch(setSelectedVariantId(variantId));
          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({ page, condition, slot, variant: updatedVariant?.name }),
            }),
          );
        }
      } finally {
        sendAckIfNeeded('SELECT_VARIANT');
      }

      return;
    }

    if (eventData.action === 'OPEN_WIDGET_EDITOR') {
      const { variantId, conditionId, slotId, page } = eventData.data;

      try {
        const currentState = store.getState() as RootState;
        const platformStoreId = currentState.app.platformStoreId;

        const tokenRes = await getToken(platformStoreId!);
        let link = `${WIDGET_EDITOR_URL}?variant=${variantId}&type=${page}&platformStoreId=${platformStoreId}&token=${tokenRes.response.token}&conditionId=${conditionId}&slotId=${slotId}&language=${i18n.language}`;

        setPageInSession(page);
        setSelectedConditionInSession(conditionId);
        setSelectedVariantInSession(variantId);

        if (tokenRes.response?.token) {
          dispatch(setIFrameLink(link));
        }
      } catch (error) { }

      return;
    }

    if (eventData.action === 'UPDATE_SLOT_NAME') {
      const { slotId, slotName } = eventData.data;

      const currentState = store.getState() as RootState;
      const selectedSlot = currentState.conditions.selectedSlot;

      if(selectedSlot?.id === slotId) {
        const page = currentState.app.activePage;
        const currentBreadcrumb = currentState.app.breadcrumbValue;
        const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
        const condition = splitBreadcrumb[1] ?? '';
        const variant = splitBreadcrumb[3] ?? '';

        dispatch(
          updateBreadcrumbValue({
            breadCrumb: generateBreadcrumbFormat({ page: page as PageType, condition, slot: slotName, variant }),
          }),
        );
      }


      dispatch(updateSlot({ id: slotId, name: slotName }));

      return;
    }

    if (eventData.action === 'ADD_SLOT') {
      const { slot, page, conditionId } = eventData.data;
      const currentState = store.getState() as RootState;
      const currentActivePage = currentState.app.activePage;
      const platformStoreId = currentState.app.platformStoreId;

      const id = slot?.id ?? slot?._id;
      const slotNo = slot?.slotNo ?? 0;

      if (id != null) {
        dispatch(
          addSlot({
            slot: { ...slot, id, _id: id, slotNo } as Partial<Slot> & { id: string; slotNo: number },
            activePage: (page as PageType) ?? currentActivePage,
            platformStoreId: platformStoreId ?? "",
          }),
        );
      }

      const variants = Array.isArray(slot?.variants) ? slot.variants : [];

      for (const variant of variants) {
        const variantId = variant?.id ?? (variant as { _id?: string })?._id;

        if (variantId) {
          try {
            const fetchedVariant = await getVariantById(variantId);

            dispatch(
              addVariant({
                conditionId,
                variant: { ...fetchedVariant, id: fetchedVariant.id ?? variantId },
              }),
            );
          } catch {
            dispatch(
              addVariant({
                conditionId,
                variant: { ...variant, id: variantId },
              }),
            );
          }
        }
      }

      return;
    }

    if (eventData.action === 'DELETE_SLOT') {
      const { page, slotNo } = eventData.data;
      const currentState = store.getState() as RootState;
      const currentActivePage = currentState.app.activePage;
      const platformStoreId = currentState.app.platformStoreId;

      const currentSelectedSlot = currentState.conditions.selectedSlot

      dispatch(
        removeSlot({
          slotNo,
          activePage: (page as PageType) ?? currentActivePage,
          platformStoreId: platformStoreId ?? "",
        }),
      );

      if(currentSelectedSlot?.slotNo === slotNo) {
        const currentBreadcrumb = currentState.app.breadcrumbValue;
        const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
        const page = currentState.app.activePage;
        const condition = splitBreadcrumb[1] ?? '';

        dispatch(
          updateBreadcrumbValue({
            breadCrumb: generateBreadcrumbFormat({ page: page as PageType, condition}),
          }),
        );

        dispatch(setSelectedSlot(null as any));
        dispatch(setSelectedVariantId(null as any));
      }

      return;
    }

    if (eventData.action === 'UPDATE_SLOT_STATUS') {
      const { active, slotId, conditionId } = eventData.data;

      dispatch(updateVariantStatus({ active, slotId, conditionId }));

      return;
    }

    if (eventData.action === 'ADD_CONDITION') {
      const { condition, page } = eventData.data;
      const conditionId = condition?.id ?? condition?._id;
      if (!conditionId) return;

      dispatch(
        addCondition({
          condition: {
            ...(condition as Partial<Condition>),
            id: String(conditionId),
            name: String(condition?.name ?? ""),
            status: Boolean(condition?.status ?? true),
            isDefault: Boolean(condition?.isDefault ?? false),
            priority: Number(condition?.priority ?? 0),
            location: (condition?.location as string) ?? page,
            rule: condition?.rule ?? {},
            scid: Number((condition as { scid?: number })?.scid ?? 0),
            storeName: String((condition as { storeName?: string })?.storeName ?? ""),
            variants: (condition as { variants?: Condition["variants"] })?.variants ?? [],
            slotConfig: (condition as { slotConfig?: Condition["slotConfig"] })?.slotConfig ?? [],
          },
        }),
      );

      dispatch(selectCondition(String(conditionId)));
      try {
        await dispatch(selectConditionAsync(String(conditionId))).unwrap();
      } catch {
        // Selection details are best-effort; list already updated.
      }

      dispatch(
        updateBreadcrumbValue({
          breadCrumb: generateBreadcrumbFormat({
            page: (page as PageType) || (condition?.location as PageType),
            condition: String(condition?.name ?? ""),
          }),
        }),
      );

      return;
    }

    if (eventData.action === 'UPDATE_CONDITION') {
      const { conditionId, condition } = eventData.data;
      if (!conditionId) return;

      dispatch(
        updateCondition({
          id: conditionId,
          name: condition?.name as string | undefined,
          status: condition?.status as boolean | undefined,
          rule: condition?.rule,
          priority: condition?.priority as number | undefined,
        }),
      );

      return;
    }

    if (eventData.action === 'DELETE_CONDITION') {
      const { conditionId } = eventData.data;
      if (!conditionId) return;

      const currentState = store.getState() as RootState;
      const remaining = (currentState.conditions.conditions || []).filter(c => c.id !== conditionId);
      const nextDefault = remaining.find(c => c.isDefault) ?? remaining[0];

      dispatch(removeCondition({ conditionId }));

      if (currentState.conditions.selectedConditionId === conditionId && nextDefault?.id) {
        dispatch(selectCondition(nextDefault.id));
        try {
          await dispatch(selectConditionAsync(nextDefault.id)).unwrap();
        } catch {
          // ignore
        }
        dispatch(
          updateBreadcrumbValue({
            breadCrumb: generateBreadcrumbFormat({
              page: nextDefault.location as PageType,
              condition: nextDefault.name,
            }),
          }),
        );
      }

      return;
    }

    if (eventData.action === 'REORDER_CONDITIONS') {
      const { priorities } = eventData.data;
      const currentState = store.getState() as RootState;
      const byId = new Map((currentState.conditions.conditions || []).map(c => [c.id, c]));
      const reordered = (priorities || [])
        .map(item => {
          const existing = byId.get(item.id);
          if (!existing) return null;
          return { ...existing, priority: item.priority };
        })
        .filter(Boolean) as Condition[];

      const leftover = (currentState.conditions.conditions || []).filter(
        c => !(priorities || []).some(p => p.id === c.id),
      );

      dispatch(updateConditionsBasedOnPriority({ conditions: [...reordered, ...leftover] }));
      return;
    }

    if (eventData.action === 'MISSING_ENTITIES') {
      const missingEntities = Array.isArray(eventData.data.missingEntities)
        ? eventData.data.missingEntities
        : [];

      const needsPicker = missingEntities.some(
        (e) =>
          e === 'product_id' ||
          e === 'product_ids' ||
          e === 'collection_id' ||
          LIST_PICKER_ENTITIES.includes(e)
      );

      if (needsPicker) {
        dispatch(openMissingEntitiesModal({ missingEntities }));
      }
    }

    if (eventData.action === 'OPEN_SUPPORT_CHAT') {
      const title = eventData.data.title ?? "Support request";
      const description = eventData.data.description ?? "";
      if(isCrisp){
        openCrispChat();
        setTimeout(() => {
          sendSupportMessage(title, description);
        }, 300);
      }
    }
  }, [dispatch, isCrisp]);

  const executeToolOutputsActionsRef = useRef(executeToolOutputsActions);

  executeToolOutputsActionsRef.current = executeToolOutputsActions;

  // --- Public handler: gates tool-output actions behind the unsaved-changes modal ---

  const processToolOutputsActions = useCallback(async (event: ToolOutputsMessageEvent) => {
    preventActionRef.current(() => executeToolOutputsActionsRef.current(event));
  }, []);

  const processToolOutputsActionsRef = useRef(processToolOutputsActions);

  return {
    skipLoadConditionsRef,
    processToolOutputsActionsRef,
  };
};

export default useAIChatContext;
